import { useMemo } from 'react';
import { DataTableProps } from 'src/components/molecules/DataTable';
import { FormatterFunc } from 'src/components/molecules/DataTable/cellFuncs';
import { DataTableModal } from 'src/components/organisms/DataTableModal';
import { TableModalRow, useColumns } from 'src/hooks/useTableModalColumns';
import { isNonNullable } from 'src/utils/Data';

type DataRow = {
  utcEpoch: number;
} & Record<string, number | null>;

type TimeseriesDataTableModalProps = {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  headerName: string;
  data: Array<DataRow>;
  yFormatter: FormatterFunc;
  dateFormatter: (value: number | string) => string;
  // CSV file and modal header name; defaults to headerName
  csvFileName?: string;
};

export const TimeseriesDataTableModal: React.FC<
  TimeseriesDataTableModalProps
> = ({
  setIsOpen,
  isOpen,
  headerName,
  yFormatter,
  dateFormatter,
  data,
  csvFileName = headerName,
}) => {
  const columns = useColumns(yFormatter);

  const dataTableProps = useMemo(() => {
    const columnKeys = columns
      .map((a) => {
        if ('accessorKey' in a) {
          return a.accessorKey;
        }
        return null;
      })
      .filter(isNonNullable);

    const props: DataTableProps<TableModalRow> = {
      columns,
      data: data.map(({ utcEpoch, ...restOfData }) => {
        const record = {
          date: dateFormatter(utcEpoch),
        } as TableModalRow;

        Object.entries(restOfData).forEach(([key, value]) => {
          if (columnKeys.some((c) => c === key)) {
            record[key] = value;
          }
        });

        return record;
      }),
      csvFileName,
    };

    return props;
  }, [columns, data, csvFileName, dateFormatter]);

  return (
    <DataTableModal
      setIsOpen={setIsOpen}
      isOpen={isOpen}
      headerName={csvFileName}
      dataTableProps={dataTableProps}
    />
  );
};
